import { themePreviewImage } from '@src/theme/previewImages';
import { usePremiumOffer } from './PremiumOfferProvider';
import { usePremiumPageCopy } from '@src/pages/PremiumPage/copy';
import { useEffect, useState } from 'react';
import { PREMIUM_THEMES, type BoxTheme } from '@lub0v/santa-utils';
import { DayNightToggle } from '@src/components/DayNightToggle/DayNightToggle';
import { Carousel } from '@src/components/Carousel/Carousel';
import { PremiumFeatureBadge } from '@src/components/PremiumBadge/PremiumFeatureBadge';
import { useTheme } from '@src/theme/useTheme';
import { loadSeason } from '@src/theme/registry';
import { SEASONS, type Season } from '@src/theme/season';
import { useTranslation } from 'react-i18next';
import { GamePreview } from '../NewBoxPage/AppearancePicker';
import { LinkIcon } from '@src/components/icons/LinkIcon';
import { StarIcon } from '@src/components/icons/StarIcon';
import { EyeOffIcon } from '@src/components/icons/EyeOffIcon';
import { LockIcon } from '@src/components/icons/LockIcon';
import space from './assets/space-preview.png';
import cyberpunk from './assets/cyberpunk-preview.png';
import alpineValley from './assets/alpine-valley-preview.png';
import tropicalIsland from './assets/tropical-island-preview.png';


const premiumPreviewOrder = SEASONS.filter(id => PREMIUM_THEMES.includes(id as BoxTheme));

export function SpacePreview() {
  const copy = usePremiumPageCopy();
  const offer = usePremiumOffer();
  const { t } = useTranslation();
  const [mode, setMode] = useState<'light' | 'dark'>('dark');
  const themes = [...premiumPreviewOrder, ...SEASONS.filter(id => !premiumPreviewOrder.includes(id))].map(id => ({
    id,
    image: themePreviewImage(id, mode),
    name: t(`theme.${id}`),
  }));
  const { seasonPackage } = useTheme();

  const [pkg, setPackage] = useState(seasonPackage);
  const [selected, setSelected] = useState<Season>(premiumPreviewOrder[0]);
  useEffect(() => {
    let active = true;
    void loadSeason(selected).then(next => { if (active) setPackage(next); });
    return () => { active = false; };
  }, [selected]);
  const renderTheme = (item: typeof themes[number]) => <button type="button" key={item.id} aria-label={item.name} aria-pressed={selected === item.id} onClick={() => setSelected(item.id)}><img src={item.image} alt="" /><span>{item.name}</span>{PREMIUM_THEMES.includes(item.id as BoxTheme) ? <PremiumFeatureBadge feature="themes">{copy.premium}</PremiumFeatureBadge> : <small className="premium-space-free">{copy.free}</small>}</button>;
  return <div className="premium-space-layout">
    <div className="premium-space-copy">
      <p className="premium-page__eyebrow">{copy.yOURSPACEYOURATMOSPHERE}</p>
      <h2>{copy.yourOwnSpaceForTheGame}</h2>
      <h3>{copy.yourAddressYourThemeYourGame}</h3>
      <p>{copy.createACozySpaceForYourCompanyFriends}</p>
      <p>{copy.yourOwnSpaceForExchangingGiftsBeautifulConvenient}</p>
      <ul className="premium-space-points">
        <li id="address"><LinkIcon /><div><strong>{copy.personalSubdomain}</strong><p>{`https://company.santa-secret.${offer.site}`}</p><small>{copy.yourGameSOwnAddressWithTheName}</small></div></li>
        <li><StarIcon /><div><strong>{copy.allThemes}</strong><p>{copy.everyAvailableAndFutureThemeSelectAThumbnail}</p></div></li>
        {!!offer.showAdsBenefit && <li><EyeOffIcon /><div><strong>{copy.noAds}</strong><p>{copy.aClearSpaceForYouAndYourParticipants}</p></div></li>}
      </ul>
    </div>
    <div id="themes" className="premium-space-demo">
      <h2 className="premium-space-themes-heading">{copy.themePreviewHeading}</h2>
      <div className="premium-space-preview-controls"><DayNightToggle value={mode} onChange={setMode} compact /></div>
      <div className="premium-space-browser">
        <div className="premium-space-toolbar"><span className="premium-space-dots" aria-hidden="true"><i /><i /><i /></span><span className="premium-space-url"><LockIcon />https://<strong>company</strong>{`.santa-secret.${offer.site}`}</span></div>
        <GamePreview pkg={pkg} name={copy.theGreatGiftCaper} picture="gift" logo={null} mode={mode} embedded selectionContext="creation" />
      </div>
      <div className="premium-space-picker" aria-label={copy.gameThemes}>
      <Carousel prevLabel={copy.previousThemes} nextLabel={copy.nextThemes} testId="premium-theme-carousel">
        {themes.filter(item => PREMIUM_THEMES.includes(item.id as BoxTheme)).map(renderTheme)}
        {themes.filter(item => !PREMIUM_THEMES.includes(item.id as BoxTheme)).map(renderTheme)}
        <button type="button" aria-label={copy.spaceThemeThumbnail} disabled><img src={space} alt="" /><span>{copy.space}</span><PremiumFeatureBadge feature="themes">{copy.premium}</PremiumFeatureBadge></button>
        <button type="button" aria-label={copy.cyberpunkThemeThumbnail} disabled><img src={cyberpunk} alt="" /><span>{copy.cyberpunk}</span><PremiumFeatureBadge feature="themes">{copy.premium}</PremiumFeatureBadge></button>
        <button type="button" aria-label={copy.alpineValleyThemeThumbnail} disabled><img src={alpineValley} alt="" /><span>{copy.alpineValley}</span><PremiumFeatureBadge feature="themes">{copy.premium}</PremiumFeatureBadge></button>
        <button type="button" aria-label={copy.tropicalIslandThemeThumbnail} disabled><img src={tropicalIsland} alt="" /><span>{copy.tropicalIsland}</span><PremiumFeatureBadge feature="themes">{copy.premium}</PremiumFeatureBadge></button>
      </Carousel>
      </div>
    </div>
  </div>;
}
